import { useState, useEffect, useCallback } from "react";
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import { db } from "@/firebase";
import { useAuth } from "@/contexts/AuthContext";
import {
  ShieldCheck,
  ShieldOff,
  UserCheck,
  UserX,
  Search,
  RefreshCw,
  Loader2,
} from "lucide-react";

export default function AdminAdmins() {
  const { user: currentUser } = useAuth();
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [updating, setUpdating] = useState(null);

  const toMs = (v) => {
    if (!v) return 0;
    if (typeof v === "string") return new Date(v).getTime() || 0;
    if (v.toDate) return v.toDate().getTime();
    if (v.seconds) return v.seconds * 1000;
    return 0;
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "users"));
      const list = snap.docs.map((d) => ({ uid: d.id, ...d.data() }));
      list.sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt));
      setAllUsers(list);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const toggleRole = async (uid, currentRole) => {
    if (uid === currentUser?.uid) return;
    setUpdating(uid);
    const newRole = currentRole === "admin" ? "user" : "admin";
    try {
      await updateDoc(doc(db, "users", uid), { role: newRole });
      setAllUsers((prev) => prev.map((u) => (u.uid === uid ? { ...u, role: newRole } : u)));
    } catch (err) { console.error("Error updating role:", err); }
    setUpdating(null);
  };

  const toggleActive = async (uid, currentActive) => {
    if (uid === currentUser?.uid) return;
    setUpdating(uid);
    const newActive = currentActive === false ? true : false;
    try {
      await updateDoc(doc(db, "users", uid), { isActive: newActive });
      setAllUsers((prev) => prev.map((u) => (u.uid === uid ? { ...u, isActive: newActive } : u)));
    } catch (err) { console.error("Error updating status:", err); }
    setUpdating(null);
  };

  // Only admins
  const admins = allUsers.filter((u) => u.role === "admin");

  const filtered = admins.filter((u) => {
    if (!search) return true;
    const s = search.toLowerCase();
    const name = (u.displayName || u.profile?.firstName || "").toLowerCase();
    const email = (u.email || u.profile?.email || "").toLowerCase();
    return name.includes(s) || email.includes(s);
  });

  const toDate = (v) => {
    if (!v) return null;
    if (v.toDate) return v.toDate();
    if (v.seconds) return new Date(v.seconds * 1000);
    if (typeof v === "string") { const d = new Date(v); return isNaN(d) ? null : d; }
    return null;
  };

  const fmtDate = (raw) => {
    const d = toDate(raw);
    if (!d) return "-";
    return d.toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "numeric" });
  };

  const fmtDateTime = (raw) => {
    const d = toDate(raw);
    if (!d) return "-";
    return d.toLocaleString("es-CO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div>
      {/* Search + Refresh */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#94A3B8" }} />
          <input
            type="text"
            placeholder="Buscar administrador..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border text-sm"
            style={{ borderColor: "#E2E8F0", background: "white", color: "#1E293B" }}
            data-testid="admins-search-input"
          />
        </div>
        <button
          onClick={fetchUsers}
          className="p-2.5 rounded-lg border transition-colors hover:bg-gray-50"
          style={{ borderColor: "#E2E8F0" }}
          data-testid="admins-refresh-btn"
        >
          <RefreshCw size={16} style={{ color: "#64748B" }} />
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin" style={{ color: "#00628F" }} />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admins-table">
              <thead>
                <tr style={{ background: "#F8FAFC" }}>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: "#475569" }}>Administrador</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: "#475569" }}>Email</th>
                  <th className="text-center px-4 py-3 font-semibold" style={{ color: "#475569" }}>Estado</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: "#475569" }}>Registro</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: "#475569" }}>Acceso</th>
                  <th className="text-center px-4 py-3 font-semibold" style={{ color: "#475569" }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => {
                  const name = u.displayName || u.profile?.firstName || "-";
                  const email = u.email || u.profile?.email || "-";
                  const active = u.isActive !== false;
                  const isSelf = u.uid === currentUser?.uid;

                  return (
                    <tr
                      key={u.uid}
                      className="border-t transition-colors hover:bg-gray-50/50"
                      style={{ borderColor: "#F1F5F9" }}
                      data-testid={`admin-row-${u.uid}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium" style={{ color: "#1E293B" }}>{name}</span>
                          {isSelf && (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: "#E0F2FE", color: "#0284C7" }}>
                              T&uacute;
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3" style={{ color: "#64748B" }}>{email}</td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full"
                          style={active ? { background: "#DCFCE7", color: "#166534" } : { background: "#FEE2E2", color: "#991B1B" }}
                          data-testid={`admin-status-${u.uid}`}
                        >
                          {active ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                      <td className="px-4 py-3" style={{ color: "#64748B" }}>{fmtDate(u.createdAt || u.profile?.createdAt)}</td>
                      <td className="px-4 py-3" style={{ color: "#64748B" }}>{fmtDateTime(u.lastLogin)}</td>
                      <td className="px-4 py-3 text-center">
                        {isSelf ? (
                          <span className="text-xs" style={{ color: "#94A3B8" }}>-</span>
                        ) : (
                          <div className="flex items-center justify-center gap-1.5">
                            {updating === u.uid ? (
                              <Loader2 size={14} className="animate-spin" style={{ color: "#94A3B8" }} />
                            ) : (
                              <>
                                <button
                                  onClick={() => toggleRole(u.uid, "admin")}
                                  title="Quitar admin"
                                  className="p-1.5 rounded-md transition-colors hover:bg-gray-100"
                                  data-testid={`remove-admin-${u.uid}`}
                                >
                                  <ShieldOff size={15} style={{ color: "#D97706" }} />
                                </button>
                                <button
                                  onClick={() => toggleActive(u.uid, u.isActive)}
                                  title={active ? "Desactivar cuenta" : "Activar cuenta"}
                                  className="p-1.5 rounded-md transition-colors hover:bg-gray-100"
                                  data-testid={`toggle-admin-active-${u.uid}`}
                                >
                                  {active ? (
                                    <UserX size={15} style={{ color: "#94A3B8" }} />
                                  ) : (
                                    <UserCheck size={15} style={{ color: "#16A34A" }} />
                                  )}
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-12" style={{ color: "#94A3B8" }}>
                      {search ? "No se encontraron administradores" : "Sin administradores registrados"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
